import { useState } from "react";
import { STATEMENT, bi, setBi } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sparkles, AlertTriangle, BookOpenCheck, CircleDot, Info, MessageSquareText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ClicheCheck } from "@/components/ClicheCheck";
import { StatementVersions } from "@/components/StatementVersions";

const QS = ["q1", "q2", "q3"];
const LIMIT = 4000;

function QuestionBlock({ idx, meta, value, lang, onChange, cvId }) {
  const len = value.length;
  const inRange = len >= meta.min && len <= meta.max;
  return (
    <div data-testid={`statement-${QS[idx]}`} className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4">
      <div className="font-bold text-[14px] text-[#1a2e4a]">{meta.q}</div>
      <div className="text-[12px] text-[#64748b] italic mt-0.5">"{meta.en}"{lang === "tr" && meta.tr ? ` — ${meta.tr}` : ""}</div>
      <Textarea data-testid={`statement-input-${QS[idx]}`} value={value} onChange={(e) => onChange(e.target.value)} rows={idx === 1 ? 10 : 7} className="mt-3 bg-white leading-relaxed" />
      <div className={`text-[11px] mt-1 text-right ${len === 0 ? "text-[#94a3b8]" : inRange ? "text-green-600" : "text-amber-600"}`}>
        {len} {lang === "tr" ? "karakter" : "characters"} · {meta.len}
      </div>
      <ClicheCheck cvId={cvId} lang={lang} question={QS[idx]} text={value} onApply={onChange} />
    </div>
  );
}

export const StatementStep = ({ cvId, cv, lang, onChange, onGenerate, generating }) => {
  const s = STATEMENT[lang];
  const st = cv.statement || {};
  const answers = st.answers || {};
  const [msg, setMsg] = useState("");
  const setField = (f, v) => onChange({ ...st, [f]: v });
  const setAnswer = (q, v) => onChange({ ...st, answers: { ...answers, [q]: setBi(answers[q], lang, v) } });
  const total = QS.reduce((n, q) => n + bi(answers[q], lang).length, 0);
  const hasDraft = QS.some((q) => bi(answers[q], lang));

  const restore = (partial) => {
    const next = { ...answers };
    for (const q of Object.keys(partial)) next[q] = setBi(answers[q], lang, partial[q] || "");
    onChange({ ...st, answers: next });
  };

  const studentAnswers = st.studentAnswers || {};
  const setStudentAnswer = (q, v) => onChange({ ...st, studentAnswers: { ...studentAnswers, [q]: setBi(studentAnswers[q], lang, v) } });

  const generate = async () => {
    if (!st.pledge) return toast.error(s.pledgeRequired);
    if (hasDraft && !window.confirm(s.confirmRegen)) return;
    setMsg("");
    const res = await onGenerate();
    if (res) setMsg(res.source === "fallback" ? s.fallback : s.generated);
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-[#64748b] leading-relaxed">{s.intro}</p>

      <div data-testid="statement-disclaimer" className="flex gap-3 rounded-lg border border-[#c0392b]/40 bg-[#fadbd8]/40 p-4 text-[13px] text-[#7b241c] leading-relaxed">
        <AlertTriangle className="h-5 w-5 shrink-0 text-[#c0392b]" />
        <span>{s.disclaimer}</span>
      </div>

      <Accordion type="single" collapsible defaultValue="guide">
        <AccordionItem value="guide" className="border rounded-lg border-[#e2e8f0] px-4">
          <AccordionTrigger data-testid="statement-guide-toggle" className="text-sm font-bold text-[#1a2e4a] hover:no-underline">
            <span className="flex items-center gap-2"><BookOpenCheck className="h-4 w-4 text-[#c0392b]" /> {s.guideTitle}</span>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            {s.questions.map((m, i) => (
              <div key={i} className="text-[13px] text-[#334155]">
                <div className="font-bold text-[#1a2e4a]">{m.q}</div>
                <div className="italic text-[#64748b]">"{m.en}"{lang === "tr" && m.tr ? ` (${m.tr})` : ""}</div>
                <div className="mt-1">{m.what}</div>
                <div className="mt-1 flex gap-1.5 text-[#64748b]"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span><b>{lang === "tr" ? "İpucu" : "Tip"}:</b> {m.tip}</span></div>
                <div className="mt-1 text-[12px] font-semibold text-[#c0392b]">{s.suggested}: {m.len}</div>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-[13px] text-[#1a2e4a]">{s.university}</Label><Input data-testid="statement-university" value={st.university || ""} onChange={(e) => setField("university", e.target.value)} placeholder={s.universityPh} className="mt-1" /></div>
        <div><Label className="text-[13px] text-[#1a2e4a]">{s.programs}</Label><Input data-testid="statement-programs" value={st.programs || ""} onChange={(e) => setField("programs", e.target.value)} placeholder={s.programsPh} className="mt-1" /></div>
        <div><Label className="text-[13px] text-[#1a2e4a]">{s.country}</Label><Input data-testid="statement-country" value={st.country || ""} onChange={(e) => setField("country", e.target.value)} className="mt-1" /></div>
        <div className="sm:col-span-2">
          <Label className="text-[13px] text-[#1a2e4a]">{s.motivation}</Label>
          <Textarea data-testid="statement-motivation" value={bi(st.motivation, lang)} onChange={(e) => setField("motivation", setBi(st.motivation, lang, e.target.value))} rows={3} placeholder={s.motivationPh} className="mt-1" />
        </div>
      </div>

      <div data-testid="statement-short-answers" className="rounded-xl border border-[#1a2e4a]/20 bg-[#eef2f8]/50 p-4 space-y-3">
        <div className="text-[14px] font-bold text-[#1a2e4a] flex items-center gap-2"><MessageSquareText className="h-4 w-4 text-[#c0392b]" /> {s.shortTitle}</div>
        <p className="text-[12.5px] text-[#64748b]">{s.shortDesc}</p>
        {s.questions.map((m, i) => (
          <div key={QS[i]}>
            <Label className="text-[13px] text-[#1a2e4a]">{m.q} <span className="font-normal text-[#64748b] italic">— "{m.en}"</span></Label>
            <Textarea data-testid={`statement-short-${QS[i]}`} value={bi(studentAnswers[QS[i]], lang)} onChange={(e) => setStudentAnswer(QS[i], e.target.value)} rows={3} placeholder={s.shortPh} className="mt-1 bg-white" />
          </div>
        ))}
      </div>

      <label data-testid="statement-pledge" className="flex items-start gap-3 rounded-lg border border-[#c0392b]/40 bg-white p-3 text-[13px] text-[#1a2e4a] cursor-pointer">
        <Checkbox data-testid="statement-pledge-checkbox" checked={!!st.pledge} onCheckedChange={(v) => setField("pledge", !!v)} className="mt-0.5" />
        <span>{s.pledge}</span>
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="statement-generate-btn" disabled={generating || !st.pledge} onClick={generate} className="bg-[#c0392b] hover:bg-[#a93226] text-white h-11">
          {generating ? <CircleDot className="h-4 w-4 mr-2 animate-pulse" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {generating ? s.generating : hasDraft ? s.regenerate : s.generate}
        </Button>
        <StatementVersions lang={lang} versions={st.versions || []} current={Object.fromEntries(QS.map((q) => [q, bi(answers[q], lang)]))} onRestore={restore} />
        {msg && <span data-testid="statement-msg" className="text-xs text-[#1a2e4a]">{msg}</span>}
      </div>

      {hasDraft && (
        <div data-testid="statement-draft-banner" className="flex gap-2 items-start rounded-lg bg-[#1a2e4a] text-white px-4 py-3 text-[13px] font-semibold">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-[#f3c9c4]" /> {s.draftBanner}
        </div>
      )}
      <div className="space-y-4">
        {s.questions.map((m, i) => (
          <QuestionBlock key={QS[i]} idx={i} meta={m} lang={lang} cvId={cvId} value={bi(answers[QS[i]], lang)} onChange={(v) => setAnswer(QS[i], v)} />
        ))}
      </div>

      <div data-testid="statement-total" className={`flex items-center justify-between text-[13px] font-semibold rounded-lg px-4 py-2.5 ${total > LIMIT ? "bg-[#fadbd8]/60 text-[#c0392b]" : "bg-[#eef2f8] text-[#1a2e4a]"}`}>
        <span>{s.total}: {total} / {LIMIT} {s.chars}</span>
        {total > LIMIT ? <span className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {s.over}</span> : <span className="text-[11px] text-[#64748b] font-normal">{s.pdfNote}</span>}
      </div>
    </div>
  );
};
